/**
 * Same-origin redirect allowlist.
 *
 * Bug-hunt #79, #80, #81: previously the SignIn / Sso components read
 * `?redirect=` from the URL and passed it straight to `navigate()` AND to
 * `window.location.href = getGoogleAuthUrl(redirectPath)`. Nothing checked
 * the target origin — an attacker who could persuade a user to click
 * `/login/signIn?redirect=https://evil.com` would land them on evil.com
 * after a successful sign-in. The OAuth `state` param had the same hole.
 *
 * Rules enforced here:
 *  - Only same-origin paths are accepted. Anything starting with a scheme
 *    (`http://`, `javascript:`, `data:`) is rejected.
 *  - Anything starting with `//` is rejected — protocol-relative URLs
 *    inherit the page's scheme but pick a new host (`//evil.com/x`).
 *  - The path must begin with a single `/` and must not contain a backslash
 *    (some browsers interpret `\/evil.com` as `//evil.com`).
 *  - The optional `allowlist` further restricts where view paths land,
 *    e.g. allow only `^/view/[A-Za-z0-9-]+$` for share links.
 *
 * Returns the validated path, or the `fallback` (default `/`) if anything
 * fails. Always returns a same-origin path — callers can pass the result
 * directly to `navigate()` or string-concatenate into a URL.
 */

export interface ValidateRedirectOptions {
  /** Used when the candidate is invalid. Defaults to "/". */
  fallback?: string;
  /**
   * Optional list of RegExps the path must match.  When omitted, any
   * same-origin absolute path is accepted.  When provided, the candidate
   * must match at least one regex.  Patterns must be anchored (^…$) — the
   * helper does not add anchors for you.
   */
  allowlist?: ReadonlyArray<RegExp>;
}

export function validateRedirect(
  candidate: string | null | undefined,
  options: ValidateRedirectOptions = {}
): string {
  const fallback = options.fallback ?? '/';
  if (typeof candidate !== 'string') return fallback;
  if (candidate.length === 0) return fallback;

  // Reject ANY whitespace or control character in the raw input. Browsers
  // and some routers silently strip newlines/tabs from URLs ("/home\n"
  // becomes "/home"), letting attackers smuggle redirects through naive
  // string checks. We refuse the whole input rather than trying to
  // normalize.
  for (let i = 0; i < candidate.length; i++) {
    const code = candidate.charCodeAt(i);
    if (code <= 0x20 || code === 0x7f) return fallback;
  }

  // Protocol-relative URLs (//evil.com/x) and absolute URLs (https://...,
  // javascript:..., data:...) escape the current origin.
  if (candidate.startsWith('//')) return fallback;
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(candidate)) return fallback;

  // Must be a rooted absolute path.
  if (!candidate.startsWith('/')) return fallback;

  // Backslash can be folded to / by some browsers, recreating //evil.com.
  if (candidate.includes('\\')) return fallback;

  // Path-segment "@-injection": `/@evil.com/x` is technically a same-origin
  // path, but some downstream code (templating, log enrichers, share-link
  // builders) will treat it as `user@host` and reach the wrong host.
  // Reject any path that starts with `/@`.
  if (candidate.startsWith('/@')) return fallback;

  // Fail closed if URL parsing throws (e.g., truly malformed input).
  try {
    // Resolve against a benign base. If the parsed origin differs from the
    // base origin, the input contained `@` or other tricks (path-segment
    // injection like `/@evil.com/x` parses as a host on some hosts).
    const base = 'https://app.example.local';
    const parsed = new URL(candidate, base);
    if (parsed.origin !== base) return fallback;

    if (options.allowlist && options.allowlist.length > 0) {
      const matchesPath = options.allowlist.some((pattern) =>
        pattern.test(parsed.pathname)
      );
      if (!matchesPath) return fallback;
      // F6: allowlist regexes are evaluated against `pathname` only, but
      // an attacker can satisfy a path regex and still smuggle a
      // dangerous host through `?next=https://evil.com` or `#u=...`.
      // When an allowlist is supplied, strip search+hash so the caller
      // receives only the path-shape that was actually vetted.
      return parsed.pathname;
    }

    return parsed.pathname + parsed.search + parsed.hash;
  } catch {
    return fallback;
  }
}

/**
 * Convenience allowlist for the `view` path family.  Pulled out so SignIn,
 * Sso, and any other caller agree on the shape.
 */
export const VIEW_PATH_ALLOWLIST: ReadonlyArray<RegExp> = [
  // /view/<sharekey>[/<link>][/] — sharekey is alnum (either case) + dash,
  // up to 64 chars: vingapi `generate_stub` builds keys from
  // `ascii_letters + digits` (32 chars), and legacy keys carry `-`. A
  // lowercase-only class sent every viewer signing in from a mixed-case key
  // to the dashboard instead of the course (#1111); the share-key contract
  // guard (src/__tests__/shareKeyPatterns.guard.test.ts) holds every client
  // share-key pattern to the server's alphabet. The optional second segment is the recipient-link token
  // (vingapi#1123, vingreact#717): case-sensitive base62, accepted here as
  // alnum + `_` + `-` up to 64 chars. The optional trailing slash is the
  // contract's canonical form `/view/{stub}/{link}/`, so a viewer signing in
  // from it lands back on it. validateRedirect tests allowlists against the
  // pathname only; the query/hash arm keeps the pattern correct for a
  // full-path caller.
  /^\/view\/[A-Za-z0-9-]{1,64}(?:\/[A-Za-z0-9_-]{1,64})?\/?(?:[?#].*)?$/,
];

/**
 * Same as validateRedirect, but pre-filtered for "is this a view share
 * URL"?  Useful when the caller wants to special-case viewers.
 */
export function isViewPath(candidate: string | null | undefined): boolean {
  return validateRedirect(candidate, { allowlist: VIEW_PATH_ALLOWLIST }) !== '/';
}
